import { Suspense, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';

import './App.scss';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-circular-progressbar/dist/styles.css';
import 'react-medium-image-zoom/dist/styles.css';
import '@fontsource/archivo-black';
import '@fontsource/dm-sans';
import AboutTab from './components/AboutTab';
import CustomIdleTimer from './components/custom_components/CustomIdleTimer';
import SubUserTab from './components/custom_components/SubUserTab/SubuserTab';
import ExamHint from './components/ExamHint';
import ForgotPassword from './components/Login/ForgotPassword';
import LoginForm from './components/Login/LoginForm';
import ResetPasswordWithOTP from './components/Login/ResetPasswordWithOTP';
import NewExam from './components/NewExam/NewExam';
import NotFound from './components/NotFound';
import SessionPreview from './components/SessionPreview/SessionPreview';
import SettingsTab from './components/SettingsTab/SettingsTab';
import useCheckClientVersion from './hooks/useCheckClientVersion';
import useGetCurrentUser from './hooks/useGetCurrentUser';
import useWatchDeployment from './hooks/useWatchDeployment';
import { CameraType } from './models/Camera.model';
import { Product } from './models/Product.model';
import DiabetesData from './products/teleophth/pages/DiabetesData/DiabetesData';
import TeleophthResults from './products/teleophth/pages/TeleophthResults/TeleophthResults';
import TeleophthStandaloneNewExam from './products/teleophthStandalone/pages/TeleophthStandaloneNewExam/TeleophthStandaloneNewExam';
import TeleophthStandaloneResults from './products/teleophthStandalone/pages/TeleophthStandaloneResults/TeleophthStandaloneResults';
import { PrivateRoute } from './routes';
import Sentry from './services/sentry';
import useRemoteBoundStore from './store/useRemoteBoundStore';

if ('$' in window) {
  window.$(() => {
    // dom ready
    window.bootbox.setDefaults({
      closeButton: false,
      animate: false,
    });
    window.bootbox.addLocale('vi', {
      OK: 'Xác nhận',
      CANCEL: 'Hủy bỏ',
      CONFIRM: 'Xác nhận',
    });
  });
}

function App() {
  useCheckClientVersion();

  const [ipList, setIpList] = useState<string[]>([]);

  const {
    authInfo: { authToken },
    practiceSettings: { product },
  } = useRemoteBoundStore();
  const { startDeploymentWatcher, stopDeploymentWatcher } =
    useWatchDeployment();

  useEffect(() => {
    // app icon
    const iconLink = document.createElement('link');
    iconLink.rel = 'icon';
    document.head.append(iconLink);
    iconLink.href = '/icons/app.ico';

    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    document.head.append(appleLink);
    appleLink.href = '/icons/app.png';

    // app name
    document.title = 'Optain Workstation';
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      startDeploymentWatcher();
    }
    return () => {
      stopDeploymentWatcher();
    };
  }, [startDeploymentWatcher, stopDeploymentWatcher]);

  const { userEmail } = useGetCurrentUser();

  Sentry.setTags({
    product: authToken ? product : null,
    cameraType: authToken ? CameraType.SMALL : null,
    userEmail,
  });

  return (
    <>
      <Suspense fallback={null}>
        <Switch>
          <Route path="/login" strict exact>
            <LoginForm />
          </Route>

          <Route path="/forgot-password" strict exact>
            <ForgotPassword />
          </Route>

          <Route path="/reset-password-with-otp/:email" strict exact>
            <ResetPasswordWithOTP />
          </Route>

          <PrivateRoute path={['/', '/session-form']} strict exact>
            {product === Product.TELEOPHTH_STANDALONE ? (
              <TeleophthStandaloneNewExam />
            ) : (
              <NewExam />
            )}
          </PrivateRoute>

          <PrivateRoute path="/session-hint" strict exact>
            <ExamHint />
          </PrivateRoute>

          <PrivateRoute path="/session-preview" strict exact>
            <SessionPreview />
          </PrivateRoute>

          <PrivateRoute path="/diabetes-data" strict exact>
            <DiabetesData />
          </PrivateRoute>

          <PrivateRoute path="/results" strict exact>
            {product === Product.TELEOPHTH_STANDALONE ? (
              <TeleophthStandaloneResults />
            ) : (
              <TeleophthResults />
            )}
          </PrivateRoute>

          <PrivateRoute path="/settings" strict exact>
            <SettingsTab ipList={ipList} setIpList={setIpList} />
          </PrivateRoute>

          <PrivateRoute path="/about" strict exact>
            <AboutTab />
          </PrivateRoute>

          <PrivateRoute path="/subuser" strict exact>
            <SubUserTab />
          </PrivateRoute>

          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </Suspense>

      {authToken && <CustomIdleTimer />}
    </>
  );
}

export default App;
